"use client";

import { createCalendar, today } from "@internationalized/date";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { useLocale, useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { useDateRangePicker, useRangeCalendar } from "react-aria";
import { useDateRangePickerState, useRangeCalendarState } from "react-stately";

import { TIMEZONE, type SearchPassengers } from "@trip/constants";
import { useDateRangeFormatter } from "@trip/react-hooks";
import { absoluteToCalendarDate, type DateRange } from "@trip/utils";

import { CalendarGrid } from "~/app/[locale]/(default)/_components/calendar";
import type { HomePageData } from "~/app/[locale]/(default)/_lib/get-home-page";
import type { SearchResultsData } from "~/app/[locale]/(default)/search/page";
import { CloseIcon } from "~/components/icons/close-icon";
import { SearchIcon } from "~/components/icons/search-icon";
import { Passengers, usePassengerTriggerLabel } from "~/components/passengers";
import { Button } from "~/components/ui/button";
import { DialogAccordion } from "~/components/ui/dialog-accordion";

import { useSearchForm, type Form } from "./use-search-form";

export function SearchMobile({
  locations,
  location,
  dateRange,
  passengers,
  tourType,
}: {
  dateRange?: DateRange | { end: number; start: number };
  location?: string;
  locations: SearchResultsData["allLocations"];
  passengers?: SearchPassengers;
  tourType?: string;
}) {
  const { fields, submit, reset } = useSearchForm({
    dateRange: dateRange
      ? {
          end:
            typeof dateRange.end === "number"
              ? absoluteToCalendarDate(dateRange.end)
              : dateRange.end,
          start:
            typeof dateRange.start === "number"
              ? absoluteToCalendarDate(dateRange.start)
              : dateRange.start,
        }
      : dateRange,
    location,
    passengers,
    tourType,
  });

  const [open, setOpen] = useState(false);

  const t = useTranslations("Search");
  const tCommon = useTranslations("Common");

  return (
    <Dialog.Root modal onOpenChange={setOpen} open={open}>
      <Dialog.Trigger asChild aria-label={t("search")}>
        <button
          type="button"
          className="shadow-card-search flex w-full items-center justify-between gap-2 rounded-full bg-neutral-200 p-2 text-left"
        >
          <div className="min-w-0 truncate px-4 leading-tight">
            <div className="text-muted text-sm font-medium">
              {t("searchTours")}
            </div>
          </div>
          <div className="shrink-0 p-1">
            <div className="shadow-button-emboss aspect-square rounded-full p-1.5 transition-all [background-image:var(--sunset-left-right)] hover:[background-image:var(--sunset-hover)]">
              <SearchIcon className="size-5 text-white" />
              <span className="sr-only">{tCommon("submit")}</span>
            </div>
          </div>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pointer-events-none! fixed inset-0 z-20 bg-neutral-200">
          <Dialog.Content
            asChild
            className="max-h-screen overflow-y-auto p-5 pb-24"
          >
            <form
              className=""
              onSubmit={(event) => {
                event.preventDefault();
                setOpen(false);
                void submit();
              }}
            >
              <div className="mb-4 flex items-center justify-between">
                <Button
                  className="border-prune-500 text-prune-500 border p-1"
                  onClick={(event) => {
                    event.preventDefault();
                    setOpen(false);
                  }}
                  size="circle"
                  variant="ghost"
                >
                  <CloseIcon />
                </Button>
                <Button
                  onClick={(event) => {
                    event.preventDefault();
                    reset();
                  }}
                  variant="link"
                >
                  Clear all
                </Button>
              </div>
              <AccordionRoot fields={fields} locations={locations} />
              <div className="absolute inset-x-0 bottom-0 z-30 bg-white px-5 py-3 shadow-[0_-0.5px_0.5px_rgba(0,0,0,0.1)]">
                <Button
                  className="w-full"
                  size="lg"
                  type="submit"
                  variant="sunset"
                >
                  {t("search")}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function AccordionRoot({
  fields,
  locations,
}: {
  fields: Form["fields"];
  locations: Omit<HomePageData["allLocations"][0], "isHero">[];
}) {
  const formatter = useDateRangeFormatter();
  const [section, setSection] = useState<
    "location" | "date-range" | "passengers"
  >("location");

  const locationLabel = locations.find(
    ({ slug }) => slug === fields.location.value,
  )?.name;

  const t = useTranslations("Search");
  const passengerTriggerLabel = usePassengerTriggerLabel();

  return (
    <DialogAccordion<typeof section>
      section={section}
      onSectionChange={(section) => {
        setSection(section);
      }}
      sections={[
        {
          children: (
            <div>
              <div className="px-5 py-3 text-2xl font-medium leading-relaxed shadow-[0_0.5px_0.5px_rgba(0,0,0,0.1)]">
                Where to?
              </div>
              <RadioGroup.Root
                className="flex max-h-[calc(100vh-420px)] flex-col items-start gap-1 overflow-y-auto px-5 py-3"
                onValueChange={(value) => {
                  fields.location.onChange(
                    typeof value === "string" ? value : "",
                  );
                  setSection("date-range");
                }}
                value={fields.location.value ?? ""}
              >
                {[{ name: t("allOfJapan"), slug: null }, ...locations].map(
                  (location) => (
                    <RadioGroup.Item
                      className="aria-checked:bg-prune-200 aria-checked:text-prune-500 hover:bg-prune-200/50 -ml-1 inline-flex items-center rounded-full px-3 py-1"
                      key={location.slug}
                      value={location.slug ?? ""}
                    >
                      <RadioGroup.Indicator />
                      {location.name}
                    </RadioGroup.Item>
                  ),
                )}
              </RadioGroup.Root>
            </div>
          ),
          cta: locationLabel ?? t("allOfJapan"),
          label: t("where"),
          value: "location",
        },
        {
          children: (
            <div className="px-5 py-3">
              <div className="mb-3 mt-1.5 text-2xl font-medium leading-relaxed">
                When’s your trip?
              </div>
              <Dates
                onChange={fields.dateRange.onChange}
                value={fields.dateRange.value}
              />
            </div>
          ),
          cta: fields.dateRange.value ? (
            <div className="inline-flex items-center gap-2">
              {formatter(fields.dateRange.value)}
              <Button
                size="circle"
                variant="ghost"
                className="-mr-2 p-0.5 text-neutral-500 hover:bg-transparent hover:text-neutral-900"
                onClick={(event) => {
                  event.preventDefault();
                  fields.dateRange.onChange(null);
                }}
              >
                <CloseIcon className="size-5" />
              </Button>
            </div>
          ) : (
            t("addDates")
          ),
          label: t("when"),
          value: "date-range",
        },
        {
          children: (
            <div className="px-5 py-3">
              <div className="mb-3 mt-1.5 text-2xl font-medium leading-relaxed">
                Who’s coming?
              </div>
              <div className="mb-2">
                <fieldset className="grid grid-cols-[1fr_max-content] items-center gap-2">
                  <Passengers
                    onChange={fields.passengers.onChange}
                    value={
                      fields.passengers.value as unknown as SearchPassengers
                    }
                  />
                </fieldset>
              </div>
            </div>
          ),
          cta: passengerTriggerLabel(fields.passengers.value),
          label: t("who"),
          value: "passengers",
        },
      ]}
    />
  );
}

function Dates({
  value,
  onChange,
}: {
  onChange: (value: DateRange | null) => void;
  value: DateRange | null;
}) {
  const [count, setCount] = useState(3);
  const ref = useRef<HTMLDivElement>(null);

  const minValue = today(TIMEZONE).add({ days: 1 });

  const state = useDateRangePickerState({
    granularity: "day",
    minValue,
    onChange,
    value,
    shouldCloseOnSelect: true,
  });

  const { calendarProps } = useDateRangePicker({ minValue }, state, ref);

  const locale = useLocale();
  const rangeState = useRangeCalendarState({
    ...calendarProps,
    createCalendar,
    locale,
    pageBehavior: "single",
    visibleDuration: { months: count },
  });

  const { calendarProps: rangeCalendarProps } = useRangeCalendar(
    calendarProps,
    rangeState,
    ref,
  );

  const t = useTranslations("Search");

  return (
    <div
      {...rangeCalendarProps}
      className="relative inline-block w-full"
      ref={ref}
    >
      <div className="flex flex-col items-stretch gap-4 [&_.title]:text-left [&_table]:w-full">
        {Array.from({ length: count }).map((_, index) => (
          <CalendarGrid
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={index}
            locale={locale}
            offset={{ months: index }}
            state={rangeState}
          />
        ))}
        <Button
          className="border-prune-500 text-prune-500 border"
          onClick={(event) => {
            event.preventDefault();
            setCount((count) => count + 1);
          }}
          variant="ghost"
        >
          {t("loadMoreDates")}
        </Button>
      </div>
    </div>
  );
}
